import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Card } from '@caruso/ui';
import { Settings, Send, Smartphone, Plus } from 'lucide-react';

export function WhatsAppIndex() {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Conexões',
      description: 'Gerencie múltiplas contas WhatsApp',
      icon: Smartphone,
      href: '/app/whatsapp/connections',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-400/10',
    },
    {
      title: 'Configuração',
      description: 'Configure integrações e agente automático',
      icon: Settings,
      href: '/app/whatsapp/config',
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
    },
    {
      title: 'Teste de Envio',
      description: 'Teste o envio de diferentes tipos de mensagens',
      icon: Send,
      href: '/app/whatsapp/test',
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-serif text-brand-gold">WhatsApp Business</h1>
        <p className="text-brand-ivory/60">
          Gerencie sua comunicação via WhatsApp
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={!feature.disabled ? { scale: 1.02 } : {}}
            >
              <Card
                className={`bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all ${
                  feature.disabled ? 'opacity-60' : 'cursor-pointer'
                }`}
                onClick={() => !feature.disabled && navigate(feature.href)}
              >
                <div className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${feature.bgColor}`}>
                      <Icon className={`h-6 w-6 ${feature.color}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-serif text-brand-gold">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-brand-ivory/60 mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={feature.disabled ? 'outline' : 'premium'}
                    disabled={feature.disabled}
                    className="w-full"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      !feature.disabled && navigate(feature.href);
                    }}
                  >
                    {feature.disabled ? 'Em breve' : 'Acessar'}
                  </Button>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-serif text-brand-gold">Sobre a Integração</h3>
            <div className="space-y-4 text-sm text-brand-ivory/60">
              <p>
                Nossa integração com WhatsApp permite que você envie mensagens automaticamente
                para seus clientes, mantendo uma comunicação eficiente e profissional.
              </p>
              <div>
                <h4 className="font-medium text-brand-ivory mb-2">Recursos disponíveis:</h4>
                <ul className="list-disc list-inside space-y-1 text-brand-ivory/50">
                  <li>Envio de mensagens de texto</li>
                  <li>Envio de imagens, vídeos e documentos</li>
                  <li>Envio de contatos e localizações</li>
                  <li>Mensagens interativas com botões e listas</li>
                  <li>Verificação de números válidos</li>
                  <li>Status em tempo real da conexão</li>
                </ul>
              </div>
              <div className="pt-2 border-t border-brand-gold/10">
                <h4 className="font-medium text-brand-ivory mb-2">Importante:</h4>
                <p className="text-brand-ivory/50">
                  Respeite as políticas do WhatsApp Business. Evite spam e envie apenas
                  mensagens relevantes para seus contatos. O uso indevido pode resultar
                  no bloqueio da sua conta.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
